import { LabelBadge } from '@/components/ui/LabelBadge';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useLabels } from '@/features/labels/labels.hooks.tsx';
import { Label, Ticket } from '@certhon/models';
import { useMemo } from 'react';

/** Type guard to filter out undefined values with proper type narrowing */
const isDefined = <T,>(value: T | undefined | null): value is T =>
  value !== undefined && value !== null;

interface LabelsCellProps {
  ticket: Ticket;
}

/**
 * Configuration for stacked label display
 */
const LABEL_CONFIG = {
  /** Maximum number of labels to display before showing "+N" */
  MAX_VISIBLE: 4,

  /** Minimum visible pixels per label (ensures border sliver is always visible) */
  MIN_VISIBLE_PX: 6,

  /**
   * Width in pixels based on label count.
   * Fewer labels get more space for better text visibility.
   */
  WIDTH_BY_COUNT: {
    1: 180, // Single label: full text visibility
    2: 180, // Two labels: still good visibility
    3: 120, // Three labels: balanced
    4: 80, // Four+ labels: compact mode
  } as Record<number, number>,

  /**
   * Overlap intensity (0-1) based on label count.
   * Controls how aggressively labels stack on each other.
   * Lower = more visible text, Higher = more compact.
   */
  OVERLAP_INTENSITY: {
    2: 0.1, // 10% - minimal overlap, show most text
    3: 0.3, // 30% - moderate overlap
    4: 0.7, // 70% - aggressive overlap for compactness
  } as Record<number, number>,
} as const;

/** Get label width based on total count */
const getLabelWidth = (total: number): number => {
  return LABEL_CONFIG.WIDTH_BY_COUNT[total] ?? LABEL_CONFIG.WIDTH_BY_COUNT[4];
};

/**
 * Calculate overlap margin for stacked labels using 1/x curve.
 * First label is anchor (no margin), subsequent labels progressively overlap.
 */
const getOverlapMargin = (index: number, total: number): number => {
  // First label is the anchor - no margin
  if (index === 0) return 0;
  // Single label - no overlap
  if (total === 1) return 0;

  const labelWidth = getLabelWidth(total);
  const intensity =
    LABEL_CONFIG.OVERLAP_INTENSITY[total] ?? LABEL_CONFIG.OVERLAP_INTENSITY[4];

  // Overlap decreases as we approach the last (top) label using 1/x curve
  const distanceFromEnd = total - index;
  const overlapFactor = 1 - 1 / (distanceFromEnd + 1);
  const overlapAmount = Math.min(
    labelWidth - LABEL_CONFIG.MIN_VISIBLE_PX,
    labelWidth * overlapFactor * intensity,
  );

  return -overlapAmount;
};

/**
 * Renders ticket labels as stacked badges with progressive overlap.
 * Used as the third column in ticket tables.
 *
 * On mobile: This becomes part of row 2 of the auto-collapsed summary cell.
 * On desktop: This is a regular column.
 */
export function LabelsCell({ ticket }: LabelsCellProps) {
  const { data: labels } = useLabels({ suspense: true });

  const ticketLabels = useMemo((): Label[] => {
    if (!labels || !ticket.labels || ticket.labels.length === 0) return [];
    return ticket.labels
      .map((labelId) => labels.find((l) => l.recnum === labelId))
      .filter(isDefined);
  }, [labels, ticket.labels]);

  if (ticketLabels.length === 0) {
    return null;
  }

  const displayedLabels = ticketLabels.slice(0, LABEL_CONFIG.MAX_VISIBLE);
  const remainingCount = ticketLabels.length - LABEL_CONFIG.MAX_VISIBLE;
  const labelWidth = getLabelWidth(displayedLabels.length);

  const stackedLabels = (
    <div className="isolate flex min-w-0 flex-1 items-center overflow-visible">
      {displayedLabels.map((label, index) => (
        <div
          key={label.recnum}
          className="[&>*]:bg-card [&>*]:ring-card [&>*]:block [&>*]:max-w-full [&>*]:truncate
            [&>*]:ring-2"
          style={{
            zIndex: index, // Last on top
            marginLeft: getOverlapMargin(index, displayedLabels.length),
            maxWidth: labelWidth,
          }}
        >
          <LabelBadge label={label} variant="outline" />
        </div>
      ))}
      {remainingCount > 0 && (
        <span className="text-muted-foreground ml-1 flex-shrink-0 text-xs">
          +{remainingCount}
        </span>
      )}
    </div>
  );

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="cursor-default">{stackedLabels}</div>
      </TooltipTrigger>
      <TooltipContent
        className="bg-popover text-popover-foreground hidden border shadow-md md:block"
        arrowClassName="bg-popover fill-popover"
        align="end"
        sideOffset={4}
      >
        <div className="flex max-w-80 flex-wrap gap-1.5 p-1">
          {ticketLabels.map((label) => (
            <LabelBadge key={label.recnum} label={label} variant="outline" />
          ))}
        </div>
      </TooltipContent>
    </Tooltip>
  );
}
